import type { Routine, Step } from '@sb/remote-control/types';

import { getStepKindInfo } from './stepKindInfo';

/**
 * Gets the sibling steps for a given step by finding its parent in the routine.
 */
export function getSiblingSteps(
  step: Step.ConvertedSummary,
  routine: Routine.ConvertedResponse,
): Step.ConvertedSummary[] {
  const parentStep = step.parentSteps.at(-1);

  if (!parentStep) {
    return routine.steps;
  }

  const findParent = (
    steps: Step.ConvertedSummary[],
  ): Step.ConvertedSummary[] | null => {
    for (const s of steps) {
      if (s.id === parentStep.id) return s.steps;

      if (s.steps.length > 0) {
        const found = findParent(s.steps);

        if (found) return found;
      }
    }

    return null;
  };

  return findParent(routine.steps) ?? [];
}

/**
 * Walk backwards through siblings to find if a position is part of a valid
 * block connection (has a 'start' step with matching blockType before it).
 *
 * This is used to validate that continuation steps (like ElseIf, Else, Case)
 * are preceded by their block's start step (like If, Switch).
 *
 * @param siblingPosition - The position of the step being validated
 * @param siblings - Array of sibling steps at the same level
 * @param blockType - The block type to look for (e.g., 'ifElse')
 * @returns true if a valid 'start' step with matching blockType is found
 *
 * @example
 * // If → ElseIf → ElseIf      → all valid
 * // ElseIf → ElseIf           → all invalid (no 'start')
 * // If → Wait → ElseIf        → ElseIf invalid (connection broken)
 * // If → ElseIf → Else → ElseIf → final ElseIf invalid (after 'end')
 */
export function isInValidBlockConnection(
  siblingPosition: number,
  siblings: Step.ConvertedSummary[],
  blockType: Step.BlockType,
): boolean {
  for (let i = siblingPosition - 1; i >= 0; i -= 1) {
    const sibling = siblings[i];
    const siblingInfo = getStepKindInfo(sibling.stepKind);
    const connector = siblingInfo.blockConnector;

    // Connection broken - step doesn't have matching blockType
    if (connector?.blockType !== blockType) {
      return false;
    }

    // Found the start of the block
    if (connector.role === 'start') {
      return true;
    }

    // Hit an 'end' step - block was already terminated, current step is orphaned
    if (connector.role === 'end') {
      return false;
    }

    // Continue walking back (it's a continuation)
  }

  // Never found a 'start' - orphaned
  return false;
}

/**
 * Validates that a step with a 'continuation' or 'end' block role is part of
 * a valid block connection. Throws an error if not.
 *
 * @param step - The step being validated
 * @param routine - The routine containing the step
 * @param blockConnector - The step's block connector configuration
 * @param errorMessage - Custom error message to throw if validation fails
 */
export function validateBlockConnectionPosition(
  step: Step.ConvertedSummary,
  routine: Routine.ConvertedResponse,
  blockConnector: Step.BlockConnector,
  errorMessage: string,
): void {
  const siblings = getSiblingSteps(step, routine);

  if (
    !isInValidBlockConnection(
      step.siblingPosition,
      siblings,
      blockConnector.blockType,
    )
  ) {
    throw new Error(errorMessage);
  }
}
